import { useEffect, useState, useMemo } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { toast } from "sonner";
import { http, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { MapPin, Users, LogIn, LogOut as LogOutIcon, RefreshCw, Loader2, Bell, BellRing, Plane, X, Star } from "lucide-react";

const CHARLOTTE = [35.2271, -80.8431];

function iconFor(status) {
  const cls = status === "Open" ? "open" : status === "Busy" ? "busy" : "full";
  const label = status === "Open" ? "OK" : status === "Busy" ? "!" : "X";
  return L.divIcon({
    className: "",
    html: `<div class="court-marker ${cls}">${label}</div>`,
    iconSize: [34, 34],
    iconAnchor: [17, 17],
  });
}

function statusPill(status) {
  const cls = status === "Open" ? "status-open" : status === "Busy" ? "status-busy" : "status-full";
  return <span className={`pill ${cls}`} data-testid={`status-${status.toLowerCase()}`}>{status}</span>;
}

function FlyTo({ pos }) {
  const map = useMap();
  useEffect(() => { if (pos) map.flyTo(pos, 13, { duration: 0.6 }); }, [pos, map]);
  return null;
}

export default function Courts() {
  const { user, refresh } = useAuth();
  const [courts, setCourts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [focus, setFocus] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await http.get("/courts");
      setCourts(data);
      setTravelPlace("");
    } catch (e) { toast.error(formatError(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [travelPlace, setTravelPlace] = useState("");

  const searchTravel = async (placeArg) => {
    const q = (typeof placeArg === "string" ? placeArg : query).trim();
    if (!q) return;
    setQuery(q);
    setSearching(true);
    try {
      const { data } = await http.get(`/courts/search?place=${encodeURIComponent(q)}`);
      setCourts(data.courts);
      setTravelPlace(data.place);
      setFocus([data.lat, data.lng]);
      if (!data.courts.length) toast.info(`No courts on PaddleVerse near ${data.place} yet — be the first to submit one!`);
    } catch (e) { toast.error(formatError(e)); }
    setSearching(false);
  };

  const savedCities = user?.saved_cities || [];
  const saveCity = async () => {
    if (!travelPlace) return;
    try { await http.post("/users/cities", { city: travelPlace }); await refresh(); toast.success(`Saved ${travelPlace}`); }
    catch (e) { toast.error(formatError(e)); }
  };
  const removeCity = async (c) => {
    try { await http.delete(`/users/cities?city=${encodeURIComponent(c)}`); await refresh(); }
    catch (e) { toast.error(formatError(e)); }
  };

  const myCourt = useMemo(() =>
    courts.find((c) => (c.players || []).some((p) => p.user_id === user?.user_id))
  , [courts, user]);

  const openDetails = async (court) => {
    try {
      const { data } = await http.get(`/courts/${court.court_id}`);
      setSelected(data);
    } catch (e) { toast.error(formatError(e)); }
  };

  const checkin = async (c) => {
    try {
      const { data } = await http.post(`/courts/${c.court_id}/checkin`);
      toast.success(data.waitlist ? "You're on the waitlist — paddle up." : "Checked in. Bring your A-game.");
      await load();
      openDetails(c);
    } catch (e) { toast.error(formatError(e)); }
  };

  const checkout = async (c) => {
    try {
      await http.post(`/courts/${c.court_id}/checkout`);
      toast.success("Checked out. Go hydrate.");
      await load();
      openDetails(c);
    } catch (e) { toast.error(formatError(e)); }
  };

  const follow = async (c) => {
    try {
      const { data } = await http.post(`/courts/${c.court_id}/follow`);
      setCourts((cs) => cs.map((x) => (x.court_id === c.court_id ? { ...x, following: data.following } : x)));
      toast.success(data.following ? `Following ${c.name} — we'll ping you when it frees up.` : `Unfollowed ${c.name}.`);
    } catch (e) { toast.error(formatError(e)); }
  };

  const stats = useMemo(() => ({
    open: courts.filter(c => c.status === "Open").length,
    busy: courts.filter(c => c.status === "Busy").length,
    full: courts.filter(c => c.status === "Full").length,
  }), [courts]);

  return (
    <div className="space-y-5" data-testid="courts-page">
      <section className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="pill pv-bg-deep-green text-white mb-2">COURT RADAR</div>
          <h1 className="text-3xl sm:text-4xl font-display font-extrabold pv-text-dark">No more ghost courts.</h1>
          <p className="text-gray-600 mt-1 text-sm">Live check-ins around Charlotte. Tap a pin, tap in, play.</p>
        </div>
        <div className="flex gap-2 text-xs">
          <span className="pill status-open">{stats.open} Open</span>
          <span className="pill status-busy">{stats.busy} Busy</span>
          <span className="pill status-full">{stats.full} Full</span>
          <button data-testid="courts-refresh" onClick={load} className="pill pv-bg-dark text-white"><RefreshCw size={12}/> Refresh</button>
        </div>
      </section>

      <section className="card p-3 flex flex-col sm:flex-row items-stretch sm:items-center gap-2" data-testid="court-travel-search">
        <div className="flex items-center gap-2 text-sm font-bold pv-text-dark shrink-0"><Plane size={16} className="text-[#F5B700]" /> Traveling?</div>
        <input
          data-testid="travel-search-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && searchTravel()}
          placeholder="Find courts in another city (e.g. Austin, TX)"
          className="flex-1 border rounded-full px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-[#BED445]"
        />
        <button data-testid="travel-search-btn" onClick={searchTravel} disabled={searching} className="rounded-full px-5 py-2 text-sm font-bold text-white pv-bg-dark disabled:opacity-50">
          {searching ? <Loader2 size={14} className="animate-spin inline" /> : "Search"}
        </button>
        {travelPlace && (
          <button data-testid="travel-reset-btn" onClick={load} className="rounded-full px-3 py-2 text-sm font-bold border border-black/10 inline-flex items-center gap-1 hover:bg-black/5">
            <X size={13} /> Near me
          </button>
        )}
      </section>
      {(savedCities.length > 0 || travelPlace) && (
        <section className="flex flex-wrap items-center gap-2" data-testid="saved-cities">
          <span className="text-xs font-bold uppercase tracking-wider text-gray-400">Saved</span>
          {savedCities.map((c) => (
            <span key={c} data-testid={`saved-city-${c}`} className="inline-flex items-center gap-1 rounded-full bg-[#0E395F]/5 border border-black/10 pl-3 pr-1 py-1 text-xs font-bold pv-text-dark">
              <button onClick={() => searchTravel(c)} data-testid={`saved-city-go-${c}`} className="hover:underline">{c}</button>
              <button onClick={() => removeCity(c)} data-testid={`saved-city-remove-${c}`} className="h-5 w-5 grid place-items-center rounded-full hover:bg-black/10"><X size={11} /></button>
            </span>
          ))}
          {travelPlace && !savedCities.includes(travelPlace) && (
            <button data-testid="save-city-btn" onClick={saveCity} className="inline-flex items-center gap-1 rounded-full bg-[#BED445] text-black px-3 py-1 text-xs font-black hover:bg-[#c9df55]">
              <Star size={12} /> Save {travelPlace}
            </button>
          )}
        </section>
      )}
      {travelPlace && <div className="text-sm text-gray-600" data-testid="travel-banner">Showing courts near <b className="pv-text-dark">{travelPlace}</b> · sorted by distance.</div>}

      <div className="grid lg:grid-cols-5 gap-5">
        <div className="lg:col-span-3 card overflow-hidden" style={{height: "440px"}} data-testid="courts-map">
          {loading ? <Loader2 className="m-8 animate-spin"/> : (
            <MapContainer center={CHARLOTTE} zoom={11} style={{height:"100%", width:"100%"}} scrollWheelZoom>
              <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"/>
              <FlyTo pos={focus} />
              {courts.map((c) => (
                <Marker key={c.court_id} position={[c.lat, c.lng]} icon={iconFor(c.status)} eventHandlers={{ click: () => openDetails(c) }}>
                  <Popup>
                    <div className="font-bold pv-text-dark">{c.name}</div>
                    <div className="text-xs">{c.active_checkins}/{c.capacity} in · {statusPill(c.status)}</div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          )}
        </div>

        <div className="lg:col-span-2 space-y-3 max-h-[440px] overflow-y-auto pr-1">
          {courts.map((c) => {
            const iAmHere = (c.players || []).some((p) => p.user_id === user?.user_id) || (myCourt?.court_id === c.court_id);
            return (
              <div key={c.court_id} className="card p-4" data-testid={`court-card-${c.court_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 cursor-pointer" onClick={() => { setFocus([c.lat, c.lng]); openDetails(c); }}>
                    <div className="font-bold pv-text-dark">{c.name}</div>
                    <div className="text-xs text-gray-500 flex items-center gap-1 mt-0.5"><MapPin size={12}/> {c.address}</div>
                    <div className="text-xs text-gray-600 mt-1 flex items-center gap-1"><Users size={12}/> {c.active_checkins}/{c.capacity} playing · {c.waitlist_count} on waitlist</div>
                  </div>
                  {statusPill(c.status)}
                </div>
                <div className="mt-3 flex gap-2">
                  {iAmHere ? (
                    <button data-testid={`checkout-btn-${c.court_id}`} onClick={()=>checkout(c)} className="btn-ghost text-sm flex items-center gap-1"><LogOutIcon size={14}/> Check out</button>
                  ) : (
                    <button data-testid={`checkin-btn-${c.court_id}`} onClick={()=>checkin(c)} className="text-sm flex items-center gap-1 rounded-full px-4 py-2 font-bold text-white" style={{background:"#005F19"}}>
                      <LogIn size={14}/> Check in
                    </button>
                  )}
                  <button data-testid={`follow-btn-${c.court_id}`} onClick={()=>follow(c)} className={`text-sm flex items-center gap-1 rounded-full px-3 py-2 font-bold border transition-colors ${c.following ? "bg-[#F5B700]/15 text-[#8a6d00] border-[#F5B700]/40" : "text-[#0E395F] border-black/10 hover:bg-black/5"}`}>
                    {c.following ? <BellRing size={14}/> : <Bell size={14}/>} {c.following ? "Following" : "Follow"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {selected && (
        <div className="card p-5" data-testid="court-detail">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-display font-extrabold text-xl pv-text-dark">{selected.name}</div>
              <div className="text-xs text-gray-500">{selected.address} · {selected.courts_count} courts</div>
            </div>
            {statusPill(selected.status)}
          </div>
          <div className="mt-4">
            <div className="text-xs font-bold uppercase text-gray-500 mb-2">Currently on court</div>
            {(selected.players || []).length === 0 ? (
              <div className="text-sm text-gray-500">Nobody's playing here. Be the first — no more ghost courts.</div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {selected.players.map((p) => (
                  <div key={p.user_id} className={`flex items-center gap-2 rounded-full pr-3 pl-1 py-1 ${p.waitlist ? "bg-yellow-50 border border-yellow-200" : "bg-[#F8F6F3] border"}`}>
                    <Avatar className="h-7 w-7"><AvatarImage src={p.avatar}/><AvatarFallback>{p.name?.[0]}</AvatarFallback></Avatar>
                    <div className="text-xs font-bold">{p.name}</div>
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full pv-bg-primary">{p.dupr_rating}</span>
                    {p.waitlist && <span className="text-[10px] font-bold pv-text-highlight uppercase">Waitlist</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
